import React, { useEffect, useRef, useState } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { useSelector } from 'react-redux';
import useSocketStore from '../../../../store/useSocketStore';

const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });

const CallDialog = ({ groupId, isVideo, onClose, members = [], isInitiator, callAnswered, isDirectMessage }) => {
    const [callStatus, setCallStatus] = useState(isInitiator && !callAnswered ? 'Ringing...' : 'Connecting...');
    const [timer, setTimer] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [isVideoOff, setIsVideoOff] = useState(!isVideo);
    const [remoteUsers, setRemoteUsers] = useState([]);
    const [showParticipants, setShowParticipants] = useState(false);

    const localVideoRef = useRef(null);
    const user = useSelector((state) => state.auth.user);
    const token = useSelector((state) => state.auth.token);
    const socket = useSocketStore(state => state.socket);

    const localTracks = useRef([]);
    const onCloseRef = useRef(onClose);

    const localDisplayName = user ? (user.name !== 'User' ? user.name : user.phoneNumber) : 'You';
    const localInitial = localDisplayName.charAt(0).toUpperCase();

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        let interval;
        if (callStatus === 'Connected') {
            interval = setInterval(() => {
                setTimer(prev => prev + 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [callStatus]);

    useEffect(() => {
        if (isInitiator && callAnswered) {
            setCallStatus('Connected');
        }
    }, [callAnswered, isInitiator]);

    useEffect(() => {
        let isUnmounted = false;

        const initAgora = async () => {
            try {
                client.on("user-published", async (user, mediaType) => {
                    await client.subscribe(user, mediaType);

                    setRemoteUsers(prev => {
                        const existing = prev.find(u => u.uid === user.uid);
                        if (existing) return prev;
                        return [...prev, user];
                    });

                    if (mediaType === "video") {
                        const remoteVideoTrack = user.videoTrack;
                        setTimeout(() => {
                            const playerContainer = document.getElementById(`video-${user.uid}`);
                            if (playerContainer && remoteVideoTrack) {
                                remoteVideoTrack.play(playerContainer);
                            }
                        }, 100);
                    }
                    if (mediaType === "audio") {
                        const remoteAudioTrack = user.audioTrack;
                        remoteAudioTrack.play();
                    }
                });

                client.on("user-left", (user) => {
                    setRemoteUsers(prev => {
                        const remaining = prev.filter(u => u.uid !== user.uid);
                        if (remaining.length === 0) {
                            setCallStatus('Call Ended');
                            setTimeout(() => {
                                if (onCloseRef.current) onCloseRef.current();
                            }, 2000);
                        }
                        return remaining;
                    });
                });

                // Since we are taking config from GroupCom, we might not have Agora App ID in this env.
                // Normally this would be fetched from process.env.VITE_AGORA_APP_ID
                // For this port, we will attempt to start the call via backend.

                const tokenRes = await fetch('http://localhost:5173/api/calls/start', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        group_id: groupId,
                        call_type: isVideo ? 'VIDEO' : 'AUDIO'
                    })
                });

                if (!tokenRes.ok) {
                    const errData = await tokenRes.json().catch(() => ({}));
                    throw new Error(errData.error || `Server error ${tokenRes.status}`);
                }

                const callData = await tokenRes.json();
                const channelName = callData.agora_channel || `group_${groupId}`;
                const rtcToken = callData.token;
                const appId = callData.appId || ''; // Backend should return this if env is missing

                if (!appId) throw new Error("Missing Agora App ID");

                const uid = Number(user.id || user._id);
                await client.join(appId, channelName, rtcToken, uid);

                if (!isInitiator || callAnswered) {
                    setCallStatus('Connected');
                }

                const tracks = [];
                const audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
                tracks.push(audioTrack);

                if (isVideo) {
                    const videoTrack = await AgoraRTC.createCameraVideoTrack();
                    tracks.push(videoTrack);
                    if (localVideoRef.current) {
                        videoTrack.play(localVideoRef.current);
                    }
                }

                if (!isUnmounted) {
                    await client.publish(tracks);
                    socket.emit('join-agora-call', { groupId, isDirectMessage });
                }

                localTracks.current = tracks;

            } catch (error) {
                console.error("Agora Error:", error);
                setCallStatus("Error: " + (error.message || "Connection failed"));
                setTimeout(() => onCloseRef.current(), 3000);
            }
        };

        initAgora();

        return () => {
            isUnmounted = true;
            localTracks.current.forEach(track => {
                track.stop();
                track.close();
            });
            localTracks.current = [];
            client.removeAllListeners();
            client.leave();
            if (socket) socket.emit('leave-agora-call', { groupId, isDirectMessage });
        };
    }, [groupId, isVideo, isDirectMessage, isInitiator, token, user]);

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const toggleAudio = () => {
        const audioTrack = localTracks.current.find(t => t.trackMediaType === 'audio');
        if (audioTrack) {
            audioTrack.setEnabled(isMuted);
            setIsMuted(!isMuted);
        }
    };

    const toggleVideo = () => {
        const videoTrack = localTracks.current.find(t => t.trackMediaType === 'video');
        if (videoTrack) {
            videoTrack.setEnabled(isVideoOff);
            setIsVideoOff(!isVideoOff);
        }
    };

    const handleEndCall = () => {
        setCallStatus('Call Ended');
        setTimeout(() => onCloseRef.current(), 1000);
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#202124', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
            {callStatus !== 'Connected' && (
                <div style={{ padding: '10px', textAlign: 'center', background: '#ea4335', color: 'white', fontWeight: 'bold' }}>
                    {callStatus}
                </div>
            )}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                <div style={{ flex: 1, padding: '20px', display: 'flex', flexWrap: 'wrap', gap: '15px', justifyContent: 'center', alignContent: 'center', overflowY: 'auto' }}>
                    <div style={{ width: '45%', height: '45%', background: '#3c4043', borderRadius: '10px', position: 'relative', overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <div ref={localVideoRef} style={{ width: '100%', height: '100%', objectFit: 'cover', display: isVideo && !isVideoOff ? 'block' : 'none', transform: 'scaleX(-1)' }}></div>
                        {(!isVideo || isVideoOff) && (
                            <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: '#0D1A3A', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '3rem', color: 'white' }}>
                                {localInitial}
                            </div>
                        )}
                        <div style={{ position: 'absolute', bottom: '15px', left: '15px', color: 'white' }}>{localDisplayName} (You)</div>
                    </div>
                    {remoteUsers.map(remoteUser => {
                        const memberInfo = members.find(m => m._id === remoteUser.uid);
                        const rDisplayName = memberInfo ? (memberInfo.name !== 'User' ? memberInfo.name : memberInfo.phoneNumber) : `User ${remoteUser.uid}`;
                        return (
                            <div key={remoteUser.uid} style={{ width: '45%', height: '45%', background: '#3c4043', borderRadius: '10px', position: 'relative', overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <div id={`video-${remoteUser.uid}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: remoteUser.hasVideo ? 'block' : 'none' }}></div>
                                {!remoteUser.hasVideo && (
                                    <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: '#1a73e8', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '3rem', color: 'white' }}>
                                        {rDisplayName.charAt(0).toUpperCase()}
                                    </div>
                                )}
                                <div style={{ position: 'absolute', bottom: '15px', left: '15px', color: 'white' }}>{rDisplayName}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div style={{ background: '#202124', padding: '15px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ color: 'white', fontSize: '1.1rem', fontWeight: '500' }}>
                    {formatTime(timer)}
                </div>
                <div style={{ display: 'flex', gap: '15px' }}>
                    <button onClick={toggleAudio} style={{ width: '50px', height: '50px', borderRadius: '50%', border: 'none', background: isMuted ? '#ea4335' : '#3c4043', color: 'white', fontSize: '1.5rem', cursor: 'pointer' }}>
                        {isMuted ? '🔇' : '🎙️'}
                    </button>
                    {isVideo && (
                        <button onClick={toggleVideo} style={{ width: '50px', height: '50px', borderRadius: '50%', border: 'none', background: isVideoOff ? '#ea4335' : '#3c4043', color: 'white', fontSize: '1.5rem', cursor: 'pointer' }}>
                            {isVideoOff ? '🚫' : '📹'}
                        </button>
                    )}
                    <button onClick={handleEndCall} style={{ width: '80px', height: '50px', borderRadius: '25px', border: 'none', background: '#ea4335', color: 'white', fontSize: '1.5rem', cursor: 'pointer' }}>
                        ☎️
                    </button>
                </div>
                <div style={{ width: '50px' }}></div>
            </div>
        </div>
    );
};

export default CallDialog;
